/**
 * SalesPipeline.tsx — Sales / Pipeline: the BOARD (S-Tier slice 3; redesign
 * slice 4, 2026-09-27, ADOPTION.md §16 — the calm-minimal Pipeline board).
 *
 * Visual authority since slice 4: docs/design/calm-minimal/Pipeline.dc.html
 * (the boards' lane geometry, sentence-case lane headers, the title row's
 * four figures, the Owner / Close date / Sort chips, the attention chip, the
 * "13d in stage" card line, the calendar footer) read into the board contract
 * through pipeline.css's `.k-pipeline--calm` block; behind it pipeline.css +
 * PIPELINE.md (the slice-0b addendum, ADOPTION.md §6).
 * Slice 4 decisions, each on a read path or omitted: "Nd in stage" is the
 * 0097 stage stamp the list already carries (daysInStage) — NOT a missing
 * field; the Board / List toggle is OMITTED (no list view exists; a dead
 * toggle is an inventory DEAD); the Lost lane STAYS (ruled: a drop target the
 * close ceremony uses); the all-time won figure is the server's closed_won
 * stage total on /v1/sales/stats; the chips filter and sort the rows the board
 * displays; the New Deal button lives in the shell's primary-action slot (2b
 * (4)) and opens the same drawer; the won lane shows every card (the boards'
 * "Show 3 more" would hide three affordances — no functionality lost is the
 * measured gate); the stage palette stays the app's (row 65).
 * Behaviour is the shipped board's, unchanged: native HTML5 drag-drop with
 * optimistic move + revert, the close ceremony as the ONLY path into closed
 * stages (drop on a closed lane or the card kebab both open CloseDealModal),
 * click-to-open Deal Detail, search filtering, per-stage create presets.
 *
 * VIEWPORT-OWNING (§6.5): this page renders inside the shell's viewportOwning
 * main (Dashboard.tsx sets it only while the BOARD is the rendered page) —
 * main stops scrolling/padding and .k-pipeline fills it; the board scrolls
 * horizontally, each lane vertically, the page never. The min-height: 0 on
 * .k-pipeline/.k-board/.k-stage/.k-lane is load-bearing (§6.5.3).
 *
 * Header figures (§6.6 data honesty): all four ride /v1/sales/reports — the
 * server's decided KPI formulas over the FULL deal set (never a derivation
 * over the limit-200 list). Since register row 84 (2026-10-02) they come from
 * the read SHARED with the sidebar's count (salesFigures.ts): a successful
 * close or create re-reads them silently, the last figures staying on screen
 * until the new ones land; an open-to-open move re-reads nothing, because it
 * changes none of them. "avg cycle" deliberately replaces PIPELINE.md's
 * "median cycle": no median exists anywhere on the read path, and the label
 * says what the number is. Lane values/shares/bars are computed from the
 * rows the board itself displays. Stage dots/bars bind to the app's pinned
 * per-stage palette (tokens stageDot) — NOT PIPELINE.md's colour table,
 * which would desync the board from every other Sales surface and map two
 * stages to one hue (recorded in ADOPTION.md §8).
 *
 * The per-deal status capsule PIPELINE.md shows (DISCOVERY/CHASE/…) has no
 * backing field and is OMITTED; the footer's right slot carries the honest
 * occupants instead — the WON/LOST outcome badge on closed cards, the
 * staleness badge on stale open ones.
 */
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  MoreHorizontal, Search, X, Briefcase, Building2,
  Layers, Calendar, User, Tag, Flag, Users, FileText, AlertCircle,
} from 'lucide-react'
import { LoadingState, ErrorState } from '../../DashboardShared'
import type { SalesDeal, DealStage } from '../../types'
import { useAsync, fetchDeals, fetchContacts, createDeal, mapDeal, moveDealStage, STAGE_TO_SLUG, cachedMeIdentity } from '../../api'
import { loadSalesFigures, refreshSalesFigures, useSalesFigures } from '../../salesFigures'
import { CurrencyGlyph, CurrencySelect } from '../../CurrencySelect'
import { clearDraft, draftKey, readDraft, writeDraft } from '../../../../lib/draftStorage'
import { currencySymbol } from '../../../../lib/money'
import { OwnerSelect } from './OwnerSelect'
import { AccountPicker } from './AccountPicker'
import { linkedContactsSentence } from './dealCreateLinks'
import { SOURCE_OPTIONS } from './vocab'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { accent, overlay, stageDot, success, danger } from '../../../../theme/tokens'
import { DealHealthBadge } from './DealHealthBadge'
import { parsePoundsToPence } from '../../../../lib/money'
import { CloseDealModal } from './CloseDealModal'
import { dropAction, isClosedStage, type CloseOutcome } from './closeFlow'
import {
  laneLabel, daysLabel, unassigned, ownerOptions, closeState, applyBoardFilters, sortDeals, attentionCount, attentionLabel, footerBadge, ageStale,
  laneFigures, shareLabel, headFigures, CLOSE_FILTERS, SORT_KEYS, type CloseFilter, type SortKey,
} from './pipelineView'

// The six mockup columns, verbatim (design note §A.2) — including Closed
// Lost, which the mockup has and the old board omitted.
const STAGES: DealStage[] = [
  'New Lead',
  'Qualified',
  'Proposal Sent',
  'Negotiation',
  'Closed Won',
  'Closed Lost',
]

// The stages a deal can be BORN into. Closed stages are reached only through
// the close ceremony (design note §C / decision #7: the server refuses closed
// stages on the bare stage route because a close carries required data and a
// Finance side effect) — a create drawer offering them would mint deals that
// bypassed it.
const OPEN_STAGES: DealStage[] = ['New Lead', 'Qualified', 'Proposal Sent', 'Negotiation']

const PRIORITY_OPTIONS = [
  { value: '',       label: 'None' },
  { value: 'low',    label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high',   label: 'High' },
] as const

const STAGE_DOT: Record<DealStage, string> = stageDot

// Lane-header + card money: compact per PIPELINE.md's derived-value rules
// (£1.2M / £980k / £0 — full precision belongs to Deal Detail only). Same
// shape as the SalesDashboard/SalesReports private copies. Per-row calls
// pass the deal's ORIGINAL currency (0111: non-GBP deals exist once
// captured); lane/board figures stay £ — they sum the GBP-comparable leg.
export function fmtCompact(pence: number, currency = 'GBP') {
  // SB-10: symbol derivation via the shared rule; a CODE keeps its
  // trailing space ("CAD 1.2M"), symbols stay unspaced — byte-identical
  // to the retired inline derivation. Pence + compaction untouched.
  const s = currencySymbol(currency)
  const sym = s === currency ? `${s} ` : s
  const n = pence / 100
  if (n >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${sym}${(n / 1_000).toFixed(0)}k`
  return `${sym}${n.toFixed(0)}`
}

function SummaryItem({ value, label }: { value: string; label: string }) {
  return (
    <span className="k-summary-item">
      <span className="k-summary-value text-ink-primary">{value}</span>
      <span className="k-summary-label">{label}</span>
    </span>
  )
}

function Field({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-12 font-semibold text-ink-body mb-2">
        <span className="text-ink-faint">{icon}</span>{label}
      </label>
      {children}
    </div>
  )
}

// ── New Deal Drawer ───────────────────────────────────────────────────────────
// Exported: the Dashboard's New Deal button reuses this drawer (P10 parity —
// the NewContactDrawer-from-SalesContacts precedent; never duplicate it).
// The mockup has no New Deal modal (its button is inert), so this is the
// house-style drawer (the Contacts NewContactDrawer template) capturing the
// mockup's full deal field set: card fields (§A.2: company, title, value,
// owner) + Deal Detail properties (§A.3: stage, value, expected close, owner,
// source, priority) + primary contact + notes.
//
// S-Tier (slice 3): the panel is .k-drawer (the foundation gap-fix's flush
// full-height sheet — width/motion stay per-consumer); picker CHIPS take the
// capsule (--r-pill, per-component adoption); text inputs/selects/textarea
// deliberately KEEP the control radius (capsules refused for inputs, slice 2).

interface NewDealDrawerProps {
  initialStage: DealStage
  onClose: () => void
  onCreated: (deal: SalesDeal) => void
}

// ── SB-8: local draft — accident recovery only. LOCAL storage, THIS form
// only; no server state, no draft stage, no pipeline visibility. ────────────
interface NewDealDraft {
  title: string; company: string; value: string; currency: string
  stage: DealStage; expectedClose: string; owner: string; source: string
  priority: '' | 'low' | 'medium' | 'high'; contactId: string; notes: string
  /** Slice A (A2.3): the picked account; '' when company is free text. */
  accountId: string
}

const NEW_DEAL_DRAFT_FORM = 'new-deal'

// "Touched" = any CONTENT field carries user input. Stage is EXCLUDED: it is
// prop-seeded by whichever add-button opened the drawer and costs one click
// to re-pick — a stage-only interaction is not worth a draft, and this rule
// is what stops an untouched stray open writing an empty draft over a real
// one.
function draftTouched(d: NewDealDraft): boolean {
  return d.title !== '' || d.company !== '' || d.value !== '' || d.currency !== 'GBP' ||
    d.expectedClose !== '' || d.owner !== '' || d.source !== '' || d.priority !== '' ||
    d.contactId !== '' || d.notes !== ''
}

export function NewDealDrawer({ initialStage, onClose, onCreated }: NewDealDrawerProps) {
  // SB-8 identity: the CACHED /v1/me (session truth — never mc:tenantId,
  // which defaults to "dev-tenant" and disagrees in WorkOS mode). Null until
  // /me resolves or if it failed — then drafts are INERT for this mount (no
  // restore, no autosave): no draft is better than one under the wrong key.
  // In practice /me resolves at dashboard boot, long before any drawer can
  // open, so the window is unobservable.
  const [dKey] = useState(() => {
    const id = cachedMeIdentity()
    return id ? draftKey(NEW_DEAL_DRAFT_FORM, id.tenantId, id.subject) : null
  })
  // Restore is a LAZY INITIAL read — an effect would flash an empty form
  // before filling it.
  const [restored] = useState(() => (dKey ? readDraft<NewDealDraft>(dKey) : null))
  const [draftNotice, setDraftNotice] = useState(restored !== null)

  const [title, setTitle]                 = useState(restored?.title ?? '')
  const [company, setCompany]             = useState(restored?.company ?? '')
  const [accountId, setAccountId]         = useState(restored?.accountId ?? '')
  const [value, setValue]                 = useState(restored?.value ?? '')
  // Increment-3 picker: deals default GBP; the server captures-or-refuses
  // a non-GBP value at create (the 2c fence).
  const [currency, setCurrency]           = useState(restored?.currency ?? 'GBP')
  // The add-button's stage WINS over the draft: it is the user's most
  // recent explicit intent, and a draft overriding the lane they just
  // clicked would make that button look broken. The other ten fields
  // restore.
  const [stage, setStage]                 = useState<DealStage>(initialStage)
  const [expectedClose, setExpectedClose] = useState(restored?.expectedClose ?? '')
  const [owner, setOwner]                 = useState(restored?.owner ?? '')
  const [source, setSource]               = useState(restored?.source ?? '')
  const [priority, setPriority]           = useState<'' | 'low' | 'medium' | 'high'>(restored?.priority ?? '')
  const [contactId, setContactId]         = useState(restored?.contactId ?? '')
  const [notes, setNotes]                 = useState(restored?.notes ?? '')
  const [saving, setSaving]               = useState(false)
  // Live mirror of the submit gate: parsePoundsToPence enforces exactly what
  // the value column takes (NUMERIC(12,2): numeric, non-negative, ≤2
  // decimals, ≤10 integer digits) — the inline error and the save toast are
  // the SAME rule, so nothing typable posts as nonsense.
  const valueInvalid = value.trim() !== '' && parsePoundsToPence(value) === null
  const contacts = useAsync(fetchContacts, [])
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  // ── SB-8 autosave ──────────────────────────────────────────────────────────
  // One debounced effect over the assembled tuple (never per-keystroke), and
  // a FLUSH on unmount — closing the drawer IS the accident this recovers,
  // so the last keystrokes must not lose a debounce race. draftDone stops
  // the flush from resurrecting a draft the successful submit just cleared.
  const latest = useRef<NewDealDraft>({ title, company, accountId, value, currency, stage, expectedClose, owner, source, priority, contactId, notes })
  latest.current = { title, company, accountId, value, currency, stage, expectedClose, owner, source, priority, contactId, notes }
  const draftDone = useRef(false)
  useEffect(() => {
    if (!dKey || !draftTouched(latest.current)) return
    const t = setTimeout(() => { if (!draftDone.current) writeDraft(dKey, latest.current) }, 400)
    return () => clearTimeout(t)
  }, [dKey, title, company, accountId, value, currency, stage, expectedClose, owner, source, priority, contactId, notes])
  useEffect(() => {
    if (!dKey) return
    return () => { if (!draftDone.current && draftTouched(latest.current)) writeDraft(dKey, latest.current) }
  }, [dKey])

  function discardDraft() {
    if (dKey) clearDraft(dKey)
    setTitle(''); setCompany(''); setAccountId(''); setValue(''); setCurrency('GBP'); setStage(initialStage)
    setExpectedClose(''); setOwner(''); setSource(''); setPriority(''); setContactId(''); setNotes('')
    setDraftNotice(false)
  }

  async function handleSave() {
    if (!title.trim()) { toast.error('Deal title is required'); return }
    if (!company.trim()) { toast.error('Company is required'); return }
    // Money crosses the boundary as integer pence (D9): parse the pounds text
    // to pence, put pence/100 on the wire's pounds field — the raw input
    // string never becomes a float.
    let pence: number | null = null
    if (value.trim() !== '') {
      pence = parsePoundsToPence(value)
      if (pence === null) { toast.error('Value must be a positive amount with up to 2 decimals, e.g. 18,000 or 9500.50'); return }
    }
    setSaving(true)
    try {
      const created = await createDeal({
        title: title.trim(),
        company: company.trim(),
        // Slice A: the picker's link travels with the create; the module
        // validates it in-tenant.
        ...(accountId ? { accountId } : {}),
        stage: STAGE_TO_SLUG[stage],
        ...(pence !== null ? { value: pence / 100 } : {}),
        // GBP is the server default; sent only when the picker moved off it.
        ...(currency !== 'GBP' ? { currency } : {}),
        ...(contactId ? { contactId } : {}),
        // Go *time.Time is strict RFC3339 — never a bare date.
        ...(expectedClose ? { expectedClose: `${expectedClose}T00:00:00Z` } : {}),
        // E360-2: OwnerSelect emits the employee ID; the server refuses a name.
        ...(owner.trim() ? { assignedEmployeeId: owner.trim() } : {}),
        ...(source.trim() ? { source: source.trim() } : {}),
        ...(priority ? { priority } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      })
      // SB-8: clear the draft on SUCCESS only. The catch path leaves the
      // form AND the draft intact — that is exactly the data the user most
      // wants back after a failed request.
      if (dKey) clearDraft(dKey)
      draftDone.current = true
      // Row 84: a create moves the open figures — re-read them for the board
      // header and the sidebar count, wherever this drawer is mounted.
      void refreshSalesFigures()
      onCreated(created)
      // §25 L-11a: the fan-out is SAID, with names — never a silent write.
      const linked = linkedContactsSentence(created.linkedContacts, created.company)
      toast.success(linked ? `Deal created · ${linked}` : 'Deal created')
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to create deal'))
    } finally {
      setSaving(false)
    }
  }

  const inputCls = "w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint transition-all"
  const chipCls = (active: boolean) =>
    `px-4 py-2 rounded-full text-13 font-semibold border transition-all ${active ? 'border-transparent text-ink-on-accent' : 'border-edge-control bg-surface-control text-ink-secondary hover:border-ink-disabled'}`

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(580px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">New Deal</div>
            <div className="text-12 text-ink-muted mt-1">Add a deal to your pipeline</div>
            {draftNotice && (
              <div className="k-card-sub mt-1">
                Restored your unsaved draft ·{' '}
                <button type="button" onClick={discardDraft} className="underline hover:text-ink-secondary">
                  Discard
                </button>
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">

          {/* Title */}
          <Field label="Deal Title *" icon={<Briefcase size={12} />}>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Full Platform Rollout" autoFocus className={inputCls} />
          </Field>

          {/* Company (full-width, the Title treatment) — Slice A (A2.3): the
              account picker; free text stays legal, a pick also links. */}
          <Field label="Company *" icon={<Building2 size={12} />}>
            <AccountPicker id="new-deal-company" value={{ accountId, company }}
              onChange={p => { setAccountId(p.accountId); setCompany(p.company) }}
              placeholder="Orbital Systems" className={inputCls} />
          </Field>

          {/* Value + Currency: two PEER grid cells (the Expected close /
              Owner pairing), never nested — the nested flex variant died on
              a w-full/w-24 Tailwind class conflict that collapsed the amount
              input. Each control is full-width in its own cell. */}
          <div className="grid grid-cols-2 gap-4">
            <Field label={`Value (${currency})`} icon={<CurrencyGlyph code={currency} />}>
              <input type="text" inputMode="decimal" value={value} onChange={e => setValue(e.target.value)}
                placeholder="18,000" className={inputCls} aria-invalid={valueInvalid} />
              {valueInvalid && (
                <div className="text-12 text-danger mt-1">
                  Enter a positive amount with up to 2 decimals, e.g. 18,000 or 9500.50
                </div>
              )}
            </Field>
            <Field label="Currency" icon={<CurrencyGlyph code={currency} />}>
              <CurrencySelect id="new-deal-currency" className={inputCls} value={currency} onChange={setCurrency} />
            </Field>
          </div>

          {/* Stage */}
          <Field label="Stage" icon={<Layers size={12} />}>
            <div className="flex items-center gap-2 flex-wrap">
              {OPEN_STAGES.map(s => {
                const active = stage === s
                return (
                  <button key={s} type="button" onClick={() => setStage(s)}
                    className={chipCls(active)}
                    style={active ? { background: accent.DEFAULT } : {}}>
                    {s}
                  </button>
                )
              })}
            </div>
          </Field>

          {/* Priority */}
          <Field label="Priority" icon={<Flag size={12} />}>
            <div className="flex items-center gap-2 flex-wrap">
              {PRIORITY_OPTIONS.map(o => {
                const active = priority === o.value
                return (
                  <button key={o.value} type="button" onClick={() => setPriority(o.value)}
                    className={chipCls(active)}
                    style={active ? { background: accent.DEFAULT } : {}}>
                    {o.label}
                  </button>
                )
              })}
            </div>
          </Field>

          {/* Expected close + Owner */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Expected Close" icon={<Calendar size={12} />}>
              <input type="date" value={expectedClose} onChange={e => setExpectedClose(e.target.value)}
                className={inputCls} />
            </Field>
            <Field label="Owner" icon={<User size={12} />}>
              {/* SB-2: picklist from employees — writes the same assigned_to
                  TEXT the Reports GROUP BY reads (no divergence possible). */}
              <OwnerSelect value={owner} onChange={setOwner} className={`${inputCls} bg-surface-card`} />
            </Field>
          </div>

          {/* Source + Primary contact */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Source" icon={<Tag size={12} />}>
              {/* SB-2: the slug vocabulary (server-validated since 0107). */}
              <select value={source} onChange={e => setSource(e.target.value)}
                className={`${inputCls} bg-surface-card`}>
                <option value="">No source</option>
                {SOURCE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Primary Contact" icon={<Users size={12} />}>
              <select value={contactId} onChange={e => setContactId(e.target.value)}
                disabled={!contacts.data} className={`${inputCls} bg-surface-card`}>
                <option value="">
                  {contacts.loading ? 'Loading contacts…' : contacts.error ? 'Contacts unavailable' : 'No contact'}
                </option>
                {(contacts.data ?? []).map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>
                ))}
              </select>
            </Field>
          </div>

          {/* Notes */}
          <Field label="Notes" icon={<FileText size={12} />}>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Add a note about this deal..."
              rows={4}
              className={`${inputCls} resize-none`} />
          </Field>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          <button onClick={onClose} className="k-btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="k-btn k-btn--lg disabled:opacity-60">
            {saving ? 'Creating…' : 'Create Deal'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Deal card (.k-deal) ───────────────────────────────────────────────────────

// Owner initials, .k-deal-owner treatment (the board's .k-avatar-on-panel2
// variant). Renders NOTHING for an unassigned deal — no fabricated owner.
function OwnerChip({ owner }: { owner: string }) {
  if (!owner.trim()) return null
  const initials = owner.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase()
  return <span className="k-deal-owner" title={owner}>{initials}</span>
}

function DealCard({ deal, onOpen, onDragStart, onDragEnd, onCloseAs, onMoveKey }: {
  deal: SalesDeal
  onOpen?: (id: string) => void
  onDragStart?: (id: string) => void
  onDragEnd?: () => void
  onCloseAs?: (deal: SalesDeal, outcome: CloseOutcome) => void
  /** Cmd/Ctrl + ←/→ (PIPELINE.md behaviour): move to the adjacent stage. */
  onMoveKey?: (deal: SalesDeal, dir: -1 | 1) => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const closed = isClosedStage(deal.stage)
  const lost = deal.stage === 'Closed Lost'
  // 2.4 (P2-12): the server's worst signal, or null for [] / null / absent —
  // slice 4: the worst of the signals OTHER than stage_stale, which the
  // "Nd in stage" line renders in place (pipelineView.footerBadge).
  const badge = footerBadge(deal.health)
  return (
    <article
      draggable={!closed}
      tabIndex={0}
      aria-label={`${deal.name}, ${deal.company}`}
      onDragStart={e => {
        onDragStart?.(deal.id)
        // Fade the SOURCE card only after the browser snapshots the drag
        // ghost — flagging it synchronously would fade the ghost too.
        const el = e.currentTarget
        requestAnimationFrame(() => el.setAttribute('data-dragging', 'true'))
      }}
      onDragEnd={e => {
        e.currentTarget.removeAttribute('data-dragging')
        onDragEnd?.()
      }}
      onClick={() => onOpen?.(deal.id)}
      onKeyDown={e => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen?.(deal.id) }
        if (!closed && (e.metaKey || e.ctrlKey) && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
          e.preventDefault()
          onMoveKey?.(deal, e.key === 'ArrowLeft' ? -1 : 1)
        }
      }}
      className={`k-deal relative group ${closed ? 'cursor-pointer' : ''} ${lost ? 'opacity-60' : ''}`}>
      <div className="k-deal-top">
        <div className="flex flex-col gap-[3px] min-w-0">
          <span className="k-deal-name text-ink-primary">{deal.name}</span>
          <span className="k-deal-company k-truncate">{deal.company}</span>
          {lost && deal.lostReason && (
            <span className="text-11 text-ink-muted">{deal.lostReason}</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Kebab = the non-drag close path (§5.B: HTML5 DnD has no touch
              support; this is also the mockup's Close-button route into
              confirmCloseDeal). Hidden on closed cards — no reopen path. */}
          {!closed && (
            <button aria-label={`Deal options for ${deal.name}`}
              className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
              onClick={e => { e.stopPropagation(); setMenuOpen(o => !o) }}>
              <MoreHorizontal size={14} className="text-ink-faint" />
            </button>
          )}
          {unassigned(deal)
            ? <span className="k-deal-owner k-deal-owner--none" title="Unassigned" aria-hidden="true" />
            : <OwnerChip owner={deal.owner} />}
        </div>
      </div>
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={e => { e.stopPropagation(); setMenuOpen(false) }} />
          <div className="absolute right-2 top-8 z-20 bg-surface-card border border-edge-control rounded-control shadow-deal-hover py-1 min-w-[150px]">
            <button className="w-full text-left px-3 py-2 text-12 text-ink-body hover:bg-surface-row-hover transition-colors"
              onClick={e => { e.stopPropagation(); setMenuOpen(false); onCloseAs?.(deal, 'won') }}>
              Close as won…
            </button>
            <button className="w-full text-left px-3 py-2 text-12 text-ink-body hover:bg-surface-row-hover transition-colors"
              onClick={e => { e.stopPropagation(); setMenuOpen(false); onCloseAs?.(deal, 'lost') }}>
              Close as lost…
            </button>
          </div>
        </>
      )}
      <div className="k-deal-amountrow">
        <span className="k-deal-amount text-ink-primary">{fmtCompact(deal.amount, deal.currency)}</span>
        <span className={`k-deal-age${ageStale(deal) ? ' k-deal-age--stale' : ''}`}>{daysLabel(deal.daysInStage)}</span>
      </div>
      {/* Ruled footer: date + the honest right-slot occupant. PIPELINE.md's
          per-deal status capsule has no backing field (omitted, not faked) —
          closed cards carry the outcome badge; open cards carry the SERVER's
          worst health signal (2.4, P2-12: at most one badge, "+N" for the
          rest), which REPLACED the card's own "> 5 days" rule so board and
          Detail can never disagree; a healthy open card carries nothing. */}
      <div className="k-deal-foot">
        {closed ? (
          <>
            <span className="k-deal-date"><Calendar size={13} aria-hidden="true" />{deal.closedDate ? `Closed ${deal.closedDate}` : ''}</span>
            <span className="k-badge"
              style={lost ? { background: danger.bg, color: danger.text } : { background: success.bg, color: success.text }}>
              <span className="k-badge-dot" />{lost ? 'LOST' : 'WON'}
            </span>
          </>
        ) : (
          <>
            {/* Slice 4: the boards' calendar footer — the date, OVERDUE by the
                server's verdict (closeState), "No close date" when the wire
                has none. */}
            <span className={`k-deal-date k-deal-date--${closeState(deal)}`}>
              <Calendar size={13} aria-hidden="true" />
              {closeState(deal) === 'missing' ? 'No close date' : closeState(deal) === 'overdue' ? `Overdue · ${deal.closeDate}` : deal.closeDate}
            </span>
            {badge && <DealHealthBadge signal={badge.signal} more={badge.more} upper />}
          </>
        )}
      </div>
    </article>
  )
}

// ── The board ─────────────────────────────────────────────────────────────────

export function SalesPipeline({ onSelectDeal, newDealOpen = false, onNewDealClose }: {
  onSelectDeal?: (id: string) => void
  /** Slice 4: the New Deal drawer's open state when opened from the shell's
   *  primary-action slot (Dashboard.tsx holds it); the per-lane "+" keeps its
   *  own preset state below. */
  newDealOpen?: boolean
  onNewDealClose?: () => void
}) {
  const [search, setSearch] = useState('')
  // Slice 4: the boards' chips — they filter and sort the rows the board displays.
  const [owner, setOwner] = useState('')
  const [close, setClose] = useState<CloseFilter>('any')
  const [sort, setSort] = useState<SortKey>('value')
  const [attentionOnly, setAttentionOnly] = useState(false)
  // The Close-date chip's "now", read once per mount (a render must not call the clock).
  const [nowMs] = useState(() => Date.now())
  const { data, loading, error } = useAsync(fetchDeals, [])
  // Header figures: the server's KPI formulas over the FULL deal set (see
  // header comment), from the read shared with the sidebar's count. A half
  // that has never landed renders a shorter summary row, never a fabricated
  // figure — .k-summary is a flex list precisely so it can. The all-time won
  // pair is the server's closed_won stage total (/v1/sales/stats, slice 4) —
  // the board's fourth figure, never a sum over the list.
  const salesFigures = useSalesFigures()
  // The board arriving (first mount, or back from Deal Detail) reads once —
  // joining the shell's read when both ask in the same commit.
  useEffect(() => { void loadSalesFigures() }, [])
  // Deals created from the drawer, newest first, MERGED with the fetched list
  // at render (derived, not synced into a copy) — a created deal lands in its
  // stage column and every derived figure (column counts/totals) recomputes
  // from the merged list.
  const [createdDeals, setCreatedDeals] = useState<SalesDeal[]>([])
  // Mutation responses (stage moves, closes) override fetched/created deals
  // at render — the same derive-don't-sync doctrine as createdDeals (§5.B.1).
  const [patched, setPatched] = useState<Record<string, SalesDeal>>({})
  // Doubles as the drawer's open flag and its preset stage (null = closed).
  const [newDealStage, setNewDealStage] = useState<DealStage | null>(null)
  // The close ceremony (a closed-column drop, or a card kebab item).
  const [closing, setClosing] = useState<{ deal: SalesDeal; outcome: CloseOutcome } | null>(null)
  // Drag state: the id travels in a ref (no re-render mid-drag); the hovered
  // column is state because it paints the drop highlight.
  const draggingId = useRef<string | null>(null)
  const [dragOver, setDragOver] = useState<DealStage | null>(null)

  // The viewportOwning main is unpadded — transitional states pad themselves.
  if (loading) return <div className="p-6 h-full overflow-y-auto"><LoadingState /></div>
  if (error) return <div className="p-6"><ErrorState message={error} /></div>

  const allDeals = [...createdDeals, ...(data ?? [])].map(d => patched[d.id] ?? d)

  // One move path for drops and keyboard moves: open target = optimistic
  // stage write + revert on error; closed target = the close ceremony.
  async function moveTo(deal: SalesDeal, target: DealStage) {
    const action = dropAction(deal.stage, target)
    if (action.kind === 'none') return
    if (action.kind === 'close') { setClosing({ deal, outcome: action.outcome }); return }
    const slug = STAGE_TO_SLUG[target]
    if (!slug) return
    // Optimistic move + revert on error (the frozen MC board's shape).
    const id = deal.id
    const prev = patched[id]
    setPatched(p => ({ ...p, [id]: { ...deal, stage: target } }))
    try {
      const wire = await moveDealStage(id, slug)
      setPatched(p => ({ ...p, [id]: mapDeal(wire) }))
      toast.success(`Moved to ${target}`)
    } catch (e) {
      setPatched(p => {
        const next = { ...p }
        if (prev) next[id] = prev
        else delete next[id]
        return next
      })
      toast.error(apiErrorMessage(e, 'Failed to move deal'))
    }
  }

  async function handleDrop(target: DealStage) {
    const id = draggingId.current
    draggingId.current = null
    setDragOver(null)
    if (!id) return
    const deal = allDeals.find(d => d.id === id)
    if (!deal) return
    await moveTo(deal, target)
  }

  const filtered = applyBoardFilters(allDeals, { search, owner, close, attentionOnly, nowMs })
  const owners = ownerOptions(allDeals)
  const attention = attentionCount(allDeals)

  // Lane figures describe what the board displays (pipelineView.laneFigures):
  // value per lane over the GBP-COMPARABLE leg (0111 — original-currency
  // pence are not summable; an uncaptured row drops out), an open lane's
  // share of the OPEN total and its bar against the largest open lane; the
  // closed lanes read all-time.
  const figures = laneFigures(filtered, STAGES)

  const head = headFigures(salesFigures.reports, salesFigures.stats)
  const r = salesFigures.reports

  return (
    <div className="k-pipeline k-pipeline--calm page-enter">
      {/* ── Title row: the boards' four figures | the page's own search ── */}
      <div className="k-pipeline-head">
        <div className="flex flex-col gap-[10px]">
          <h1 className="k-pipeline-title text-ink-primary">Pipeline</h1>
          <div className="k-summary" aria-label="Pipeline figures">
            {r && <>
              <SummaryItem value={fmtCompact(head.openPence)} label="open" />
              <SummaryItem value={String(head.openCount)} label={head.openCount === 1 ? 'deal' : 'deals'} />
              <SummaryItem value={head.avgPence != null ? fmtCompact(head.avgPence) : '—'} label="average" />
            </>}
            {head.won && <SummaryItem value={fmtCompact(head.won.pence)} label={`won · ${head.won.count} ${head.won.count === 1 ? 'deal' : 'deals'}${head.won.excluded ? ' ' + head.won.excluded : ''}`} />}
          </div>
        </div>
        {/* The boards put the deal search in the bar's capsule; 2b (5) ruled the
            capsule out of the shell, so the page keeps its own search here. The
            Board / List toggle is OMITTED (no list view exists); New Deal is the
            shell's slot button (Dashboard.tsx). */}
        <div className="k-search k-search--compact">
          <Search size={13} className="flex-shrink-0" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search deals…"
            className="flex-1 min-w-0 bg-transparent border-0 p-0 text-ink-body placeholder:text-ink-faint"
            style={{ fontSize: 'inherit' }}
          />
        </div>
      </div>

      {/* ── Filters: the boards' three chips + the attention chip ── */}
      <div className="k-pipeline-filters">
        <label className="k-chip"><span className="k-chip-key">Owner</span>
          <select aria-label="Owner" value={owner} onChange={e => setOwner(e.target.value)}>
            <option value="">Anyone</option>
            <option value="—">Unassigned</option>
            {owners.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="k-chip"><span className="k-chip-key">Close date</span>
          <select aria-label="Close date" value={close} onChange={e => setClose(e.target.value as CloseFilter)}>
            {CLOSE_FILTERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="k-chip"><span className="k-chip-key">Sort</span>
          <select aria-label="Sort" value={sort} onChange={e => setSort(e.target.value as SortKey)}>
            {SORT_KEYS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <span className="flex-1" />
        {attention > 0 && (
          <button type="button" className="k-chip k-chip--attention" aria-pressed={attentionOnly} onClick={() => setAttentionOnly(v => !v)}>
            <AlertCircle size={14} aria-hidden="true" />{attentionLabel(attention)}
          </button>
        )}
      </div>

      {/* ── Board: lanes scroll vertically, the board horizontally ── */}
      <div className="k-board">
        {STAGES.map(stage => {
          const stageDeals = sortDeals(filtered.filter(d => d.stage === stage), sort)
          const fig = figures.get(stage) ?? { count: 0, valuePence: 0, sharePct: 0, barPct: 0 }
          const open = OPEN_STAGES.includes(stage)
          const won = stage === 'Closed Won', lost = stage === 'Closed Lost'
          return (
            <section key={stage} className={`k-stage${won ? ' k-stage--won' : lost ? ' k-stage--lost' : ''}`}
              aria-label={`${stage} stage`}
              onDragOver={e => { e.preventDefault(); if (dragOver !== stage) setDragOver(stage) }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(cur => (cur === stage ? null : cur)) }}
              onDrop={e => { e.preventDefault(); void handleDrop(stage) }}>
              <div className="k-stage-head">
                <div className="k-stage-titlerow">
                  <div className="k-stage-name">
                    <span className="k-stage-dot" style={{ background: STAGE_DOT[stage] }} />
                    <h2 className="k-stage-label k-truncate text-ink-primary">{laneLabel(stage)}</h2>
                    <span className="k-stage-count">{stageDeals.length}</span>
                  </div>
                  {/* Open lanes' + preset the create drawer to their stage; the
                      closed lanes keep the stub — a deal is never born closed
                      (the close ceremony's business, not create's). */}
                  <button className="k-stage-add" aria-label={`Add deal to ${stage}`}
                    onClick={() => open ? setNewDealStage(stage) : toast.info('Add deal coming soon')}>
                    +
                  </button>
                </div>
                <div className="k-stage-metrics">
                  <span className="k-stage-value text-ink-primary">{fmtCompact(fig.valuePence)}</span>
                  <span className={`k-stage-share${won ? ' k-stage-share--won' : ''}`}>{shareLabel(stage, fig.sharePct)}</span>
                </div>
                <div className="k-stage-bar">
                  <div style={{ width: `${fig.barPct}%`, background: STAGE_DOT[stage] }} />
                </div>
              </div>

              <div className="k-lane" data-dropactive={dragOver === stage || undefined}>
                {stageDeals.length === 0 ? (
                  <div className="k-lane-empty">
                    {search || owner || close !== 'any' || attentionOnly ? 'No matching deals' : 'No deals in this stage'}
                    {open && !search && !owner && close === 'any' && !attentionOnly && (
                      <button onClick={() => setNewDealStage(stage)}>+ ADD DEAL</button>
                    )}
                  </div>
                ) : (
                  stageDeals.map(deal => (
                    <DealCard key={deal.id} deal={deal} onOpen={onSelectDeal}
                      onDragStart={id => { draggingId.current = id }}
                      onDragEnd={() => { draggingId.current = null; setDragOver(null) }}
                      onCloseAs={(d, outcome) => setClosing({ deal: d, outcome })}
                      onMoveKey={(d, dir) => {
                        const idx = STAGES.indexOf(d.stage)
                        const target = STAGES[idx + dir]
                        if (target) void moveTo(d, target)
                      }} />
                  ))
                )}
              </div>
            </section>
          )
        })}
      </div>

      {/* Drawer */}
      {(newDealOpen || newDealStage) && (
        <NewDealDrawer
          initialStage={newDealStage ?? 'New Lead'}
          onClose={() => { setNewDealStage(null); onNewDealClose?.() }}
          onCreated={deal => setCreatedDeals(prev => [deal, ...prev])}
        />
      )}

      {/* Close ceremony (§5.C) — the response is the updated wire deal;
          patching it in re-derives every column count/total. */}
      {closing && (
        <CloseDealModal
          deal={{ id: closing.deal.id, title: closing.deal.name, company: closing.deal.company, amountPence: closing.deal.amount, currency: closing.deal.currency }}
          outcome={closing.outcome}
          onCancel={() => setClosing(null)}
          onClosed={wire => {
            setPatched(p => ({ ...p, [wire.id]: mapDeal(wire) }))
            setClosing(null)
          }}
        />
      )}
    </div>
  )
}
